import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from 'flowbite-react';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'superAdmin' | 'organizationAdmin' | 'viewer';
}

/**
 * Wrapper component that protects routes requiring authentication.
 * Redirects to login page if user is not authenticated.
 * Optionally checks for specific role requirements.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
}) => {
  const { isAuthenticated, isLoading, currentUser, authError } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner size="xl" color="info" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If there's a domain error, redirect to access denied
  if (authError === 'domain_not_allowed') {
    return <Navigate to="/access-denied" replace />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements if specified
  if (requiredRole && currentUser) {
    const roleHierarchy = ['viewer', 'organizationAdmin', 'superAdmin'];
    const userRoleIndex = roleHierarchy.indexOf(currentUser.role);
    const requiredRoleIndex = roleHierarchy.indexOf(requiredRole);

    if (userRoleIndex < requiredRoleIndex) {
      return (
        <Navigate
          to="/access-denied"
          state={{
            message: 'You do not have permission to access this page.',
            suggestion: `This page requires ${requiredRole} access.`,
          }}
          replace
        />
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
